import {
	type AnalyticsEventPayload,
	type AiSuggestionInteractionPoint,
	type AiSuggestionsEntryPoint,
	type AiSuggestionsEventPayload,
	type AiSuggestionsRegenerationOutcome,
	type AiSuggestionsRegenerationTrigger,
	type EditorAnalyticsAPI,
	type AIUnifiedEventPayload,
	EVENT_TYPE,
	ACTION,
	ACTION_SUBJECT,
	ACTION_SUBJECT_ID,
} from '@atlaskit/editor-common/analytics';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import type { Transaction } from '@atlaskit/editor-prosemirror/state';

import type {
	AiSuggestionsPlugin,
	AiSuggestionsSharedState,
	Suggestion,
	ToolCall,
} from '../../aiSuggestionsPluginType';

type AdfNode = {
	attrs?: { localId?: unknown };
	content?: unknown;
	type?: unknown;
};

// Walks an inserted ADF subtree to collect every `localId` and node `type`. Captures localIds at
// every depth because tool-call collisions can be triggered by nested ids, not just the root.
function collectFromAdf(node: AdfNode | undefined, localIds: Set<string>, nodeTypes: Set<string>) {
	if (!node || typeof node !== 'object') {
		return;
	}
	if (typeof node.type === 'string') {
		nodeTypes.add(node.type);
	}
	const localId = node.attrs?.localId;
	if (typeof localId === 'string') {
		localIds.add(localId);
	}
	if (Array.isArray(node.content)) {
		for (const child of node.content) {
			collectFromAdf(child as AdfNode, localIds, nodeTypes);
		}
	}
}

function collectFromToolCall(toolCall: ToolCall, localIds: Set<string>, nodeTypes: Set<string>) {
	if (toolCall.name === 'removeNodes') {
		for (const id of toolCall.arguments.localIds) {
			localIds.add(id);
		}
		return;
	}
	localIds.add(toolCall.arguments.localId);
	if ('value' in toolCall.arguments && typeof toolCall.arguments.value === 'string') {
		try {
			collectFromAdf(JSON.parse(toolCall.arguments.value) as AdfNode, localIds, nodeTypes);
		} catch {
			// Ignore malformed JSON - the tool call would also fail to apply, surfaced elsewhere.
		}
	}
}

function collectToolCallAttributes(toolCall: ToolCall) {
	const localIds = new Set<string>();
	const nodeTypes = new Set<string>();
	collectFromToolCall(toolCall, localIds, nodeTypes);
	return {
		name: toolCall.name,
		localIds: Array.from(localIds),
		nodeTypes: Array.from(nodeTypes),
	};
}

function getErrorCodeAttributes(errorAttributes?: { errorCode?: unknown; statusCode?: unknown }) {
	const { errorCode, statusCode } = errorAttributes ?? {};
	return {
		...(typeof errorCode === 'string' ? { errorCode } : {}),
		...(typeof statusCode === 'number' ? { statusCode } : {}),
	};
}

/**
 * Attach analytics when applying a suggestion's tool calls produces no semantic doc change.
 * Surfaces silent "no diff" cases (duplicate-localId collisions, unresolvable targets, self-
 * cancelling step pairs). Caller decides when to invoke based on `appliedTr.before.eq(appliedTr.doc)`.
 */
export const addNoDiffAnalytics =
	(editorAnalyticsAPI: EditorAnalyticsAPI | undefined) =>
	(transaction: Transaction, suggestion: Suggestion): Transaction => {
		const toolCalls = (suggestion.toolCalls ?? []).map(collectToolCallAttributes);

		const payload = {
			action: ACTION.NO_DIFF_FOUND,
			actionSubject: ACTION_SUBJECT.AI_SUGGESTIONS,
			eventType: EVENT_TYPE.OPERATIONAL,
			attributes: {
				suggestionType: suggestion.type,
				toolCalls,
			},
		} as AnalyticsEventPayload;
		editorAnalyticsAPI?.attachAnalyticsEvent(payload)(transaction);
		return transaction;
	};

/**
 * Returns the number of unique ADF blocks affected by a suggestion.
 */
export const getAffectedBlocks = (suggestion: Suggestion): number => {
	const ids = new Set<string>();
	for (const localId of suggestion.action?.localIds ?? []) {
		ids.add(localId);
	}
	for (const toolCall of suggestion.toolCalls ?? []) {
		if (toolCall.name === 'removeNodes') {
			for (const id of toolCall.arguments.localIds) {
				ids.add(id);
			}
		} else {
			ids.add(toolCall.arguments.localId);
		}
	}
	return ids.size;
};

export const getTimeSinceGenerationMs = (
	suggestion: Suggestion,
	now: number = Date.now(),
): number | undefined => {
	const createdAt = Date.parse(suggestion.createdAt);
	return Number.isFinite(createdAt) ? Math.max(0, now - createdAt) : undefined;
};

const getTimeSinceGenerationAttribute = (
	suggestion: Suggestion,
	now?: number,
): { timeSinceGenerationMs?: number } => {
	const timeSinceGenerationMs = getTimeSinceGenerationMs(suggestion, now);
	return timeSinceGenerationMs === undefined ? {} : { timeSinceGenerationMs };
};

const getSuggestionRemixAttributes = (
	suggestion: Suggestion,
): { remixSubtype?: string; remixType?: string } => {
	if (suggestion.actionKind !== 'SUGGESTED_REMIX') {
		return {};
	}
	return {
		...(suggestion.action?.launchOptions?.dynamicUiType
			? { remixType: suggestion.action.launchOptions.dynamicUiType }
			: {}),
		...(suggestion.action?.launchOptions?.dynamicUiSubtype
			? { remixSubtype: suggestion.action.launchOptions.dynamicUiSubtype }
			: {}),
	};
};

const getSuggestionCommonAttributes = (suggestion: Suggestion, now?: number) => {
	return {
		actionKind: suggestion.actionKind ?? 'SUGGESTED_EDIT',
		agentId: suggestion.createdByAgent?.accountId,
		...getTimeSinceGenerationAttribute(suggestion, now),
		...getSuggestionRemixAttributes(suggestion),
	};
};

/**
 * Returns the total plain text characters being added by a suggestion.
 * Derived from the `value` ADF JSON of inserting/replacing tool calls.
 */
function getTextFromAdfNode(node: AdfNode & { text?: string }): string {
	if (node.type === 'text' && typeof node.text === 'string') {
		return node.text;
	}
	if (Array.isArray(node.content)) {
		return node.content.map((child) => getTextFromAdfNode(child as AdfNode)).join('');
	}
	return '';
}

export const getCharactersAdded = (suggestion: Suggestion): number => {
	let count = 0;
	for (const toolCall of suggestion.toolCalls ?? []) {
		if ('value' in toolCall.arguments && typeof toolCall.arguments.value === 'string') {
			try {
				count += getTextFromAdfNode(JSON.parse(toolCall.arguments.value) as AdfNode).length;
			} catch {
				// malformed JSON — skip
			}
		}
	}
	return count;
};

/**
 * Returns the total plain text characters being removed by a suggestion.
 * Must be called before applySuggestion so the original doc content is intact.
 * Uses ProseMirror's built-in node.textContent for accurate plain text extraction.
 */
export const getCharactersRemoved = (suggestion: Suggestion, doc: PMNode): number => {
	const removingCalls = ['removeNode', 'removeNodes', 'replaceNode'];
	const visitedIds = new Set<string>();
	let count = 0;
	for (const toolCall of suggestion.toolCalls ?? []) {
		if (!removingCalls.includes(toolCall.name)) {
			continue;
		}
		const localIds =
			toolCall.name === 'removeNodes' ? toolCall.arguments.localIds : [toolCall.arguments.localId];
		for (const localId of localIds) {
			if (visitedIds.has(localId)) {
				continue;
			}
			visitedIds.add(localId);
			doc.descendants((node) => {
				if (node.attrs.localId === localId) {
					count += node.textContent.length;
					return false;
				}
			});
		}
	}
	return count;
};

/**
 * Attach analytics when a user clicks an entry point that starts suggested edits.
 */
export const addEntryPointClickedAnalytics =
	(editorAnalyticsAPI: EditorAnalyticsAPI | undefined) =>
	(transaction: Transaction, entryPoint: AiSuggestionsEntryPoint): Transaction => {
		const payload: AiSuggestionsEventPayload = {
			action: ACTION.CLICKED,
			actionSubject: ACTION_SUBJECT.AI_SUGGESTIONS,
			eventType: EVENT_TYPE.TRACK,
			attributes: {
				entryPoint,
			},
		};
		editorAnalyticsAPI?.attachAnalyticsEvent(payload)(transaction);
		return transaction;
	};

/**
 * Attach a `clicked` event to the given transaction when the user clicks the
 * "Try again" / retry button on the suggested edits error screen (the thinking
 * bar's error state).
 */
export const addRetryThinkingAnalytics =
	(editorAnalyticsAPI: EditorAnalyticsAPI | undefined) =>
	(transaction: Transaction): Transaction => {
		const payload: AiSuggestionsEventPayload = {
			action: ACTION.CLICKED,
			actionSubject: ACTION_SUBJECT.AI_SUGGESTIONS,
			actionSubjectId: ACTION_SUBJECT_ID.SUGGESTIONS_ERROR_RETRY,
			eventType: EVENT_TYPE.TRACK,
		};
		editorAnalyticsAPI?.attachAnalyticsEvent(payload)(transaction);
		return transaction;
	};

/**
 * Fire analytics when a user can visually see an entry point that starts suggested edits.
 */
export const fireEntryPointExposureAnalytics = (
	editorAnalyticsAPI: EditorAnalyticsAPI | undefined,
	entryPoint: AiSuggestionsEntryPoint,
): void => {
	const payload: AiSuggestionsEventPayload = {
		action: ACTION.EXPOSED,
		actionSubject: ACTION_SUBJECT.AI_SUGGESTIONS,
		eventType: EVENT_TYPE.TRACK,
		attributes: {
			entryPoint,
		},
	};
	editorAnalyticsAPI?.fireAnalyticsEvent(payload);
};

/**
 * Fire analytics when the initial Rovo conversation setup for suggested edits fails.
 */
export const fireConversationSetupErrorAnalytics = (
	editorAnalyticsAPI: EditorAnalyticsAPI | undefined,
	error: unknown,
): void => {
	const errorAttributes = (
		error as {
			errorAttributes?: {
				errorCode?: unknown;
				statusCode?: unknown;
			};
		}
	)?.errorAttributes;
	const payload: AiSuggestionsEventPayload = {
		action: ACTION.ERRORED,
		actionSubject: ACTION_SUBJECT.AI_SUGGESTIONS,
		actionSubjectId: ACTION_SUBJECT_ID.CONVERSATION_ERROR,
		eventType: EVENT_TYPE.OPERATIONAL,
		attributes: {
			reason: 'conversationSetup',
			...getErrorCodeAttributes(errorAttributes),
		},
	};
	editorAnalyticsAPI?.fireAnalyticsEvent(payload);
};

/**
 * Fire analytics when the suggested edits stream reports an in-stream error.
 */
export const fireStreamErrorAnalytics = (
	editorAnalyticsAPI: EditorAnalyticsAPI | undefined,
	error: unknown,
): void => {
	const message = (
		error as {
			message?: {
				key?: unknown;
				status_code?: unknown;
			};
		}
	)?.message;
	const payload: AiSuggestionsEventPayload = {
		action: ACTION.ERRORED,
		actionSubject: ACTION_SUBJECT.AI_SUGGESTIONS,
		actionSubjectId: ACTION_SUBJECT_ID.CONVERSATION_ERROR,
		eventType: EVENT_TYPE.OPERATIONAL,
		attributes: {
			reason: 'streamError',
			...getErrorCodeAttributes({
				errorCode: message?.key,
				statusCode: message?.status_code,
			}),
		},
	};
	editorAnalyticsAPI?.fireAnalyticsEvent(payload);
};

/**
 * Fire analytics when provisioning the OOTB agent that owns the suggested edits skill fails.
 */
export const fireAgentProvisioningErrorAnalytics = (
	editorAnalyticsAPI: EditorAnalyticsAPI | undefined,
	error: unknown,
): void => {
	const errorAttributes = (
		error as {
			errorAttributes?: {
				errorCode?: unknown;
				statusCode?: unknown;
			};
		}
	)?.errorAttributes;
	const payload: AiSuggestionsEventPayload = {
		action: ACTION.ERRORED,
		actionSubject: ACTION_SUBJECT.AI_SUGGESTIONS,
		actionSubjectId: ACTION_SUBJECT_ID.CONVERSATION_ERROR,
		eventType: EVENT_TYPE.OPERATIONAL,
		attributes: {
			reason: 'agentProvisioning',
			...getErrorCodeAttributes(errorAttributes),
		},
	};
	editorAnalyticsAPI?.fireAnalyticsEvent(payload);
};

/**
 * Fire analytics when the OOTB agent cannot be resolved from its external config
 * reference. The lookup reports its own failures and resolves empty, so there is no
 * error object to attach — the absence of an agent is the signal.
 */
export const fireAgentExternalConfigReferenceErrorAnalytics = (
	editorAnalyticsAPI: EditorAnalyticsAPI | undefined,
): void => {
	const payload: AiSuggestionsEventPayload = {
		action: ACTION.ERRORED,
		actionSubject: ACTION_SUBJECT.AI_SUGGESTIONS,
		actionSubjectId: ACTION_SUBJECT_ID.CONVERSATION_ERROR,
		eventType: EVENT_TYPE.OPERATIONAL,
		attributes: {
			reason: 'agentExternalConfigReference',
		},
	};
	editorAnalyticsAPI?.fireAnalyticsEvent(payload);
};

/**
 * Fire analytics when the suggested edits Rovo agent is deactivated.
 */
export const fireAgentDeactivatedErrorAnalytics = (
	editorAnalyticsAPI: EditorAnalyticsAPI | undefined,
): void => {
	const payload: AiSuggestionsEventPayload = {
		action: ACTION.ERRORED,
		actionSubject: ACTION_SUBJECT.AI_SUGGESTIONS,
		actionSubjectId: ACTION_SUBJECT_ID.CONVERSATION_ERROR,
		eventType: EVENT_TYPE.OPERATIONAL,
		attributes: {
			reason: 'agentDeactivated',
		},
	};
	editorAnalyticsAPI?.fireAnalyticsEvent(payload);
};

const getRegenerationSuggestionDetails = (suggestion: Suggestion, now: number) => ({
	actionKind: suggestion.actionKind ?? 'SUGGESTED_EDIT',
	...getTimeSinceGenerationAttribute(suggestion, now),
	...getSuggestionRemixAttributes(suggestion),
	suggestionId: suggestion.id,
	suggestionType: suggestion.type,
});

export const startRegenerateSuggestionsAnalytics = (
	editorAnalyticsAPI: EditorAnalyticsAPI | undefined,
	{
		currentNodeCount,
		staleSuggestionIds,
		suggestions,
		trigger,
	}: {
		currentNodeCount: number;
		staleSuggestionIds: ReadonlySet<string>;
		suggestions: Suggestion[];
		trigger: AiSuggestionsRegenerationTrigger;
	},
): {
	completed: (options: {
		regeneratedSuggestions: Suggestion[];
		shouldRetainStale: boolean;
	}) => void;
	errored: (error: unknown) => void;
} => {
	const startedAt = Date.now();
	const staleSuggestions = suggestions.filter((suggestion) =>
		staleSuggestionIds.has(suggestion.id),
	);
	const payload: AiSuggestionsEventPayload = {
		action: ACTION.REGENERATION_STARTED,
		actionSubject: ACTION_SUBJECT.AI_SUGGESTIONS,
		actionSubjectId: ACTION_SUBJECT_ID.SUGGESTIONS_REGENERATION,
		eventType: EVENT_TYPE.OPERATIONAL,
		attributes: {
			staleSuggestionCount: staleSuggestions.length,
			currentNodeCount,
			suggestionDetails: staleSuggestions.map((suggestion) =>
				getRegenerationSuggestionDetails(suggestion, startedAt),
			),
			trigger,
		},
	};
	editorAnalyticsAPI?.fireAnalyticsEvent(payload);

	return {
		completed: ({ regeneratedSuggestions, shouldRetainStale }) => {
			const regeneratedById = new Map(
				regeneratedSuggestions.map((suggestion) => [suggestion.id, suggestion]),
			);
			const outcomes = staleSuggestions.map((suggestion) => {
				const regeneratedSuggestion = regeneratedById.get(suggestion.id);
				let outcome: AiSuggestionsRegenerationOutcome;
				if (regeneratedSuggestion?.status === 'STALE') {
					outcome = 'stillStale';
				} else if (regeneratedSuggestion) {
					outcome = 'updated';
				} else {
					outcome = shouldRetainStale ? 'stillStale' : 'removed';
				}
				return { outcome, suggestion };
			});
			const suggestionDetails = outcomes.map(({ outcome, suggestion }) => ({
				...getRegenerationSuggestionDetails(suggestion, startedAt),
				outcome,
			}));
			const completedPayload: AiSuggestionsEventPayload = {
				action: ACTION.REGENERATION_COMPLETED,
				actionSubject: ACTION_SUBJECT.AI_SUGGESTIONS,
				actionSubjectId: ACTION_SUBJECT_ID.SUGGESTIONS_REGENERATION,
				eventType: EVENT_TYPE.OPERATIONAL,
				attributes: {
					staleSuggestionCount: outcomes.length,
					currentNodeCount,
					regenerationDurationMs: Math.max(0, Date.now() - startedAt),
					removedSuggestionCount: outcomes.filter(({ outcome }) => outcome === 'removed').length,
					stillStaleSuggestionCount: outcomes.filter(({ outcome }) => outcome === 'stillStale')
						.length,
					updatedSuggestionCount: outcomes.filter(({ outcome }) => outcome === 'updated').length,
					suggestionDetails,
					trigger,
				},
			};
			editorAnalyticsAPI?.fireAnalyticsEvent(completedPayload);
		},
		errored: (error) => {
			const errorAttributes = (
				error as {
					errorAttributes?: {
						errorCode?: unknown;
						statusCode?: unknown;
					};
				}
			)?.errorAttributes;
			const errorPayload: AiSuggestionsEventPayload = {
				action: ACTION.ERRORED,
				actionSubject: ACTION_SUBJECT.AI_SUGGESTIONS,
				actionSubjectId: ACTION_SUBJECT_ID.SUGGESTIONS_REGENERATION_ERROR,
				eventType: EVENT_TYPE.OPERATIONAL,
				attributes: {
					staleSuggestionCount: staleSuggestions.length,
					currentNodeCount,
					regenerationDurationMs: Math.max(0, Date.now() - startedAt),
					suggestionDetails: staleSuggestions.map((suggestion) =>
						getRegenerationSuggestionDetails(suggestion, startedAt),
					),
					trigger,
					...getErrorCodeAttributes(errorAttributes),
				},
			};
			editorAnalyticsAPI?.fireAnalyticsEvent(errorPayload);
		},
	};
};

export const startSuggestionsGenerationAnalytics = (
	api: ExtractInjectionAPI<AiSuggestionsPlugin>,
	trigger: AiSuggestionsRegenerationTrigger,
): {
	completed: (options: { regenerateOnly: boolean; suggestions: Suggestion[] }) => void;
} => {
	const refreshStartedAt = Date.now();
	const stateBeforeRefresh = api.aiSuggestions?.sharedState.currentState();
	const existingSuggestionIds = new Set(stateBeforeRefresh?.orderedSuggestionIds ?? []);
	const generationStartedAt = stateBeforeRefresh?.thinkingStartedAt ?? refreshStartedAt;

	return {
		completed: ({ regenerateOnly, suggestions }) => {
			if (regenerateOnly || trigger !== 'reviewCompleted') {
				return;
			}
			const generatedSuggestions = suggestions.filter(
				(suggestion) => !existingSuggestionIds.has(suggestion.id),
			);
			const payload: AiSuggestionsEventPayload = {
				action: ACTION.GENERATED,
				actionSubject: ACTION_SUBJECT.AI_SUGGESTIONS,
				eventType: EVENT_TYPE.TRACK,
				attributes: {
					generationDurationMs: Math.max(0, Date.now() - generationStartedAt),
					hadExistingSuggestions: existingSuggestionIds.size > 0,
					numberOfSuggestions: generatedSuggestions.length,
					suggestionDetails: generatedSuggestions.map((suggestion) => ({
						actionKind: suggestion.actionKind ?? 'SUGGESTED_EDIT',
						affectedBlocks: getAffectedBlocks(suggestion),
						agentId: suggestion.createdByAgent?.accountId,
						hasSources: Boolean(suggestion.sources?.length),
						...getSuggestionRemixAttributes(suggestion),
						suggestionId: suggestion.id,
						suggestionType: suggestion.type,
					})),
				},
			};
			api.analytics?.actions.fireAnalyticsEvent(payload);
		},
	};
};

const SUGGESTED_EDITS_EXPERIENCE_NAME = 'Suggested edits';

/**
 * Fire the unified `aiInteraction initiated` event
 *
 * @param editorAnalyticsAPI - The editor analytics API (from `api.analytics.actions`).
 * @param entryPoint - Entry point that triggered the interaction. Reported as `invokedFrom`.
 * @param singleInstrumentationId - Unique per-request ID; populates
 *   `singleInstrumentationID` to correlate the journey's unified events.
 */
export const fireAiInteractionInitiatedUnifiedAnalytics = (
	editorAnalyticsAPI: EditorAnalyticsAPI | undefined,
	entryPoint: AiSuggestionsEntryPoint,
	singleInstrumentationId: string,
): void => {
	const payload: AIUnifiedEventPayload = {
		action: ACTION.INITIATED,
		actionSubject: ACTION_SUBJECT.AI_INTERACTION,
		actionSubjectId: ACTION_SUBJECT_ID.EDITOR_PLUGIN_AI,
		eventType: EVENT_TYPE.TRACK,
		attributes: {
			aiFeatureName: 'Editor AI',
			aiExperienceName: SUGGESTED_EDITS_EXPERIENCE_NAME,
			singleInstrumentationID: singleInstrumentationId,
			isAIFeature: 1,
			// TODO: EDITOR-8150 - userGeneratedAI / proactiveAIGenerated are hardcoded for
			// the current user-triggered flow; parameterise these once proactive
			// suggestions are supported.
			userGeneratedAI: 1,
			proactiveAIGenerated: 0,
			invokedFrom: entryPoint,
		},
	};
	editorAnalyticsAPI?.fireAnalyticsEvent(payload);
};

/**
 * Attach analytics when a suggestion is discarded/rejected.
 * Mirrors addAcceptSuggestionAnalytics but for the discard case.
 * No doc-content measurements are needed since no change is applied to the document.
 */
export const addDiscardSuggestionAnalytics =
	(editorAnalyticsAPI: EditorAnalyticsAPI | undefined) =>
	(
		transaction: Transaction,
		suggestion: Suggestion,
		interactionPoint: AiSuggestionInteractionPoint,
	): Transaction => {
		const payload: AiSuggestionsEventPayload = {
			action: ACTION.DISCARDED,
			actionSubject: ACTION_SUBJECT.AI_SUGGESTIONS,
			eventType: EVENT_TYPE.TRACK,
			attributes: {
				...getSuggestionCommonAttributes(suggestion),
				affectedBlocks: getAffectedBlocks(suggestion),
				hasSources: Boolean(suggestion.sources?.length),
				suggestionId: suggestion.id,
				suggestionType: suggestion.type,
				interactionPoint,
			},
		};
		editorAnalyticsAPI?.attachAnalyticsEvent(payload)(transaction);
		return transaction;
	};

/**
 * Attach one aggregate event describing every suggestion removed by Discard all.
 */
export const addDiscardAllSuggestionsAnalytics =
	(editorAnalyticsAPI: EditorAnalyticsAPI | undefined) =>
	(transaction: Transaction, suggestions: Suggestion[]): Transaction => {
		const payload: AiSuggestionsEventPayload = {
			action: ACTION.DISCARD_ALL,
			actionSubject: ACTION_SUBJECT.AI_SUGGESTIONS,
			eventType: EVENT_TYPE.TRACK,
			attributes: {
				numberOfSuggestions: suggestions.length,
				suggestionDetails: suggestions.map((suggestion) => ({
					...getSuggestionCommonAttributes(suggestion),
					affectedBlocks: getAffectedBlocks(suggestion),
					hasSources: Boolean(suggestion.sources?.length),
					interactionPoint: 'sidebar',
					suggestionId: suggestion.id,
					suggestionType: suggestion.type,
				})),
			},
		};
		editorAnalyticsAPI?.attachAnalyticsEvent(payload)(transaction);
		return transaction;
	};

/**
 * Fire the unified `aiFeedback submitted` event when a user submits feedback
 * (good / bad) on a suggestion via the "Good suggestion" / "Bad suggestion"
 * (thumbs up / thumbs down) controls.
 *
 * This is the canonical thumbs-feedback event consumed by the AI feedback
 * pipeline (`analyticsplat.event_track` -> `agg_ai_thumbs_feedback_daily`),
 * where `aiFeedbackResult` maps to the `vote` (up/down) dimension. See the
 * "Feedback Datasets & Dashboard" and "Suggested Edits - Unified Event
 * Instrumentation Proposal" specs.
 *
 * @param editorAnalyticsAPI - The editor analytics API (from `api.analytics.actions`).
 * @param sentiment - The user's feedback: `good` (thumbs up) or `bad` (thumbs down).
 * @param singleInstrumentationId - The suggestion id, used to populate
 *   `singleInstrumentationID`.
 * @param interactionPoint - Where the feedback was given (card / sidebar).
 *   Reported as `invokedFrom`.
 *
 * TODO: EDITOR-8269 - Use the backend-assigned instrumentation id (piped through
 * the skill) for `singleInstrumentationID` instead of the suggestion id, so
 * feedback correlates to the interaction that produced the suggestion.
 */
export const fireSubmitSuggestionFeedbackUnifiedAnalytics = (
	editorAnalyticsAPI: EditorAnalyticsAPI | undefined,
	sentiment: 'good' | 'bad',
	singleInstrumentationId: string,
	interactionPoint: AiSuggestionInteractionPoint,
): void => {
	const payload: AIUnifiedEventPayload = {
		action: ACTION.SUBMITTED,
		actionSubject: ACTION_SUBJECT.AI_FEEDBACK,
		actionSubjectId: ACTION_SUBJECT_ID.EDITOR_PLUGIN_AI,
		eventType: EVENT_TYPE.TRACK,
		attributes: {
			aiFeatureName: 'Editor AI',
			aiExperienceName: SUGGESTED_EDITS_EXPERIENCE_NAME,
			singleInstrumentationID: singleInstrumentationId,
			isAIFeature: 1,
			// TODO: EDITOR-8150 - userGeneratedAI / proactiveAIGenerated are hardcoded for
			// the current user-triggered flow; parameterise these once proactive
			// suggestions are supported.
			userGeneratedAI: 1,
			proactiveAIGenerated: 0,
			invokedFrom: interactionPoint,
			aiFeedbackResult: sentiment === 'good' ? 'up' : 'down',
		},
	};
	editorAnalyticsAPI?.fireAnalyticsEvent(payload);
};

/**
 * Attach analytics when a suggestion is collapsed/minimised.
 * This is distinct from discard/reject: the suggestion remains available.
 */
export const addDismissSuggestionAnalytics =
	(editorAnalyticsAPI: EditorAnalyticsAPI | undefined) =>
	(
		transaction: Transaction,
		suggestion: Suggestion,
		interactionPoint: AiSuggestionInteractionPoint,
	): Transaction => {
		const payload: AiSuggestionsEventPayload = {
			action: ACTION.DISMISSED,
			actionSubject: ACTION_SUBJECT.AI_SUGGESTIONS,
			eventType: EVENT_TYPE.TRACK,
			attributes: {
				...getSuggestionCommonAttributes(suggestion),
				affectedBlocks: getAffectedBlocks(suggestion),
				hasSources: Boolean(suggestion.sources?.length),
				suggestionId: suggestion.id,
				suggestionType: suggestion.type,
				interactionPoint,
			},
		};
		editorAnalyticsAPI?.attachAnalyticsEvent(payload)(transaction);
		return transaction;
	};

export const addDismissAnalyticsForSelectionChange = ({
	currentState,
	editorAnalyticsAPI,
	interactionPoint,
	nextSuggestionId,
	transaction,
}: {
	currentState: AiSuggestionsSharedState | undefined;
	editorAnalyticsAPI: EditorAnalyticsAPI | undefined;
	interactionPoint: AiSuggestionInteractionPoint | undefined;
	nextSuggestionId: string | null;
	transaction: Transaction;
}): Transaction => {
	if (!interactionPoint) {
		return transaction;
	}

	const previousSelectedSuggestionId = currentState?.selectedSuggestion;
	const previousSelectedSuggestion = previousSelectedSuggestionId
		? currentState?.suggestionById.get(previousSelectedSuggestionId)
		: undefined;

	const isClearingSelectedSuggestion = nextSuggestionId === null;
	const isReplacingSelectedSuggestion =
		nextSuggestionId !== null && currentState?.suggestionById.has(nextSuggestionId) === true;
	const shouldTrackDismissedSuggestion =
		previousSelectedSuggestionId !== undefined &&
		previousSelectedSuggestionId !== nextSuggestionId &&
		(isClearingSelectedSuggestion || isReplacingSelectedSuggestion);

	const dismissedSuggestion = shouldTrackDismissedSuggestion
		? previousSelectedSuggestion
		: undefined;

	if (dismissedSuggestion) {
		addDismissSuggestionAnalytics(editorAnalyticsAPI)(
			transaction,
			dismissedSuggestion,
			interactionPoint,
		);
	}

	return transaction;
};

/**
 * Returns the block-level ADF node types affected by a suggestion.
 * Uses the ProseMirror schema (via doc.type.schema) to determine block-ness
 */
export const getBlockTypes = (suggestion: Suggestion, doc: PMNode): string[] => {
	const localIds = new Set<string>(suggestion.action?.localIds ?? []);
	const nodeTypes = new Set<string>();
	for (const toolCall of suggestion.toolCalls ?? []) {
		collectFromToolCall(toolCall, localIds, nodeTypes);
	}

	// Look up removed nodes in the doc by localId to capture their types (removeNodes has no ADF value).
	doc.descendants((node) => {
		if (typeof node.attrs?.localId === 'string' && localIds.has(node.attrs.localId)) {
			nodeTypes.add(node.type.name);
		}
	});

	const schema = doc.type.schema;
	return Array.from(nodeTypes).filter((typeName) => schema.nodes[typeName]?.isBlock === true);
};

/**
 * Attach analytics when a suggestion is viewed/opened.
 * Fires when the user explicitly opens a suggestion — either by clicking the
 * sparkle icon button in the editor or by expanding a suggestion in the comments panel list.
 * Must be called inside core.actions.execute before any doc mutations so
 * tr.doc still has the original content for computing charactersToRemove.
 */
export const addViewSuggestionAnalytics =
	(editorAnalyticsAPI: EditorAnalyticsAPI | undefined) =>
	(
		transaction: Transaction,
		suggestion: Suggestion,
		interactionPoint: AiSuggestionInteractionPoint,
	): Transaction => {
		const isSuggestedRemix = suggestion.actionKind === 'SUGGESTED_REMIX';
		const payload: AiSuggestionsEventPayload = {
			action: ACTION.VIEWED,
			actionSubject: ACTION_SUBJECT.AI_SUGGESTIONS,
			eventType: EVENT_TYPE.TRACK,
			attributes: {
				...getSuggestionCommonAttributes(suggestion),
				affectedBlocks: getAffectedBlocks(suggestion),
				blockTypes: getBlockTypes(suggestion, transaction.doc),
				...(!isSuggestedRemix && {
					charactersToAdd: getCharactersAdded(suggestion),
					charactersToRemove: getCharactersRemoved(suggestion, transaction.doc),
				}),
				hasSources: Boolean(suggestion.sources?.length),
				interactionPoint,
				suggestionCardCharacterCount: suggestion.shortDescription?.length ?? 0,
				suggestionId: suggestion.id,
				suggestionType: suggestion.type,
			},
		};
		editorAnalyticsAPI?.attachAnalyticsEvent(payload)(transaction);
		return transaction;
	};

/**
 * Fire analytics when a user expands the reasoning for a suggestion.
 */
export const fireSuggestionReasoningViewedAnalytics = (
	editorAnalyticsAPI: EditorAnalyticsAPI | undefined,
	suggestion: Suggestion,
	interactionPoint: AiSuggestionInteractionPoint,
): void => {
	const payload: AiSuggestionsEventPayload = {
		action: ACTION.REASONING_VIEWED,
		actionSubject: ACTION_SUBJECT.AI_SUGGESTIONS,
		eventType: EVENT_TYPE.TRACK,
		attributes: {
			...getSuggestionCommonAttributes(suggestion),
			affectedBlocks: getAffectedBlocks(suggestion),
			hasSources: Boolean(suggestion.sources?.length),
			interactionPoint,
			reasoningCharacterCount: suggestion.reasoning?.length ?? 0,
			suggestionId: suggestion.id,
			suggestionType: suggestion.type,
		},
	};
	editorAnalyticsAPI?.fireAnalyticsEvent(payload);
};

/** Fire once for each accepted suggestion restored by an editor undo. */
export const fireAcceptedSuggestionUndoAnalytics = (
	editorAnalyticsAPI: EditorAnalyticsAPI | undefined,
	suggestion: Suggestion,
): void => {
	const payload: AiSuggestionsEventPayload = {
		action: ACTION.UNDO_PERFORMED,
		actionSubject: ACTION_SUBJECT.AI_SUGGESTIONS,
		eventType: EVENT_TYPE.TRACK,
		attributes: {
			actionKind: suggestion.actionKind ?? 'SUGGESTED_EDIT',
			...getTimeSinceGenerationAttribute(suggestion),
			suggestionId: suggestion.id,
			suggestionType: suggestion.type,
		},
	};
	editorAnalyticsAPI?.fireAnalyticsEvent(payload);
};

/** Fire analytics when the user stops an in-progress suggestions review. */
export const fireSuggestionCancelledAnalytics = (
	editorAnalyticsAPI: EditorAnalyticsAPI | undefined,
	interactionPoint: AiSuggestionInteractionPoint,
): void => {
	const payload: AiSuggestionsEventPayload = {
		action: ACTION.CANCELLED,
		actionSubject: ACTION_SUBJECT.AI_SUGGESTIONS,
		eventType: EVENT_TYPE.TRACK,
		attributes: { interactionPoint },
	};
	editorAnalyticsAPI?.fireAnalyticsEvent(payload);
};

/**
 * Attach analytics when a suggestion is accepted.
 * Must be called inside core.actions.execute before applySuggestion,
 * so tr.doc still has the original content for computing charactersRemoved.
 */
export const addAcceptSuggestionAnalytics =
	(editorAnalyticsAPI: EditorAnalyticsAPI | undefined) =>
	(
		transaction: Transaction,
		suggestion: Suggestion,
		interactionPoint: AiSuggestionInteractionPoint,
		isDiffHidden = false,
	): Transaction => {
		const isSuggestedRemix = suggestion.actionKind === 'SUGGESTED_REMIX';
		const payload: AiSuggestionsEventPayload = {
			action: ACTION.ACCEPTED,
			actionSubject: ACTION_SUBJECT.AI_SUGGESTIONS,
			eventType: EVENT_TYPE.TRACK,
			attributes: {
				...getSuggestionCommonAttributes(suggestion),
				affectedBlocks: getAffectedBlocks(suggestion),
				...(!isSuggestedRemix && {
					charactersAdded: getCharactersAdded(suggestion),
					charactersRemoved: getCharactersRemoved(suggestion, transaction.doc),
				}),
				hasSources: Boolean(suggestion.sources?.length),
				isDiffHidden,
				suggestionId: suggestion.id,
				suggestionType: suggestion.type,
				interactionPoint,
			},
		};
		editorAnalyticsAPI?.attachAnalyticsEvent(payload)(transaction);
		return transaction;
	};
